import React, { useState, useMemo, useRef } from 'react';
import { Button, Input, Form, message } from 'antd';
import { removeExtraSpaces } from '@helpers/stringTransformers';
import { toggleManageFolderAccessModal } from '@redux/actions/modalAction';
import { createFolderAction } from '@redux/actions/assetsAction';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { CampaignItemDto } from '@api/Api';
import { DiamondUpgradeIcon, EyePrivateIcon, EyePublicIcon } from '@icons';

type FolderCreationProps = {
  campaign: CampaignItemDto;
  parentId?: string;
  onCloseModal: () => void;
  handlerFinish?: ({
    name,
    isPrivate
  }: {
    name: string;
    isPrivate: boolean;
  }) => void;
};

export default function FolderCreation({
  campaign,
  parentId,
  onCloseModal,
  handlerFinish
}: FolderCreationProps) {
  const dispatch = useTypedDispatch();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const { currentOrganization } = useOrganization();

  const [nameValueLength, setNameValueLength] = useState<number>(0);

  const isSubmittingRef = useRef(false);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValueLength = e.target.value.length;
    setNameValueLength(newValueLength);
  };

  const canCreatePrivate =
    !!campaign?.permissions?.createPrivateFolders &&
    !!currentOrganization.entity?.features.privateFolders;

  const onFinish = async ({ name }: { name: string }) => {
    try {
      if (isSubmitting) return;
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      const newName = removeExtraSpaces(name);
      setIsSubmitting(true);
      if (handlerFinish) {
        await handlerFinish({ name, isPrivate });
        onCloseModal();
        return;
      }
      const folder = await dispatch(
        createFolderAction({
          campaignId: campaign.id,
          parentId: parentId || null,
          name: newName,
          isPrivate
        })
      );
      if (folder.isPrivate)
        dispatch(toggleManageFolderAccessModal({ folder, visible: true }));
      onCloseModal();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || 'Something went wrong'
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  const isUpgradeCanVisible = useMemo(() => {
    return !currentOrganization.entity?.features.privateFolders;
  }, [currentOrganization.entity?.features.privateFolders]);

  return (
    <div className="modal_container enitity-creation-modal campaign-folder-creation-modal-container">
      <Form form={form} onFinish={onFinish} validateTrigger="onChange">
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 32 }}
        >
          New folder
        </h2>
        <Form.Item
          className="enitity-creation-modal__name-item enitity-creation-modal__name-item--counter"
          name="name"
          rules={[
            {
              whitespace: true,
              message: "'name' cannot be empty."
            },
            {
              max: 60,
              message: `${nameValueLength}/60 characters. Name has exceeded character limit.`
            },
            {
              pattern: /^[a-zA-Z 0-9 (".,:'\-—/!?#+=*@&©)]*$/,
              message: `Please use only letters, numbers or the following special symbols .,:()'-—/!?#+=*@&`
            },
            {
              required: true,
              message: 'Name is required.'
            }
          ]}
          style={{ margin: '32px 0px 24px', height: 78 }}
        >
          <Input
            onChange={onNameChange}
            placeholder="Add folder name"
            size="large"
            showCount={{
              formatter: ({ count }) => (
                <span className="main-body-text main-body-text--semibold main-body-text--small main-body-text--dark-gray">
                  {count}/60 characters
                </span>
              )
            }}
            maxLength={60}
          />
        </Form.Item>
        <div className="modal_container__campaign">
          <p className="copy">
            {' '}
            <span>folder</span> access
          </p>
          <div className="types">
            <button
              type="button"
              className={`type_block type_block--public ${
                !isPrivate ? 'type_block_selected' : ''
              }`}
              onClick={() => setIsPrivate(false)}
            >
              <div className="title">
                <EyePublicIcon />
                Shared folder
              </div>
              <div className="description">
                All project members can access this folder
              </div>
            </button>
            <UpgradePlanTooltip
              text=" to a Team plan to access this feature."
              isCanVisible={isUpgradeCanVisible}
              innerStyle={{
                transform: 'translate(-20px, 25px)'
              }}
            >
              <div>
                <button
                  type="button"
                  className={`type_block type_block--private ${
                    isPrivate ? 'type_block_selected' : ''
                  } ${!canCreatePrivate ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!currentOrganization.subscription) return;
                    if (!canCreatePrivate) return;
                    setIsPrivate(true);
                  }}
                >
                  <div className="title">
                    <EyePrivateIcon /> Private folder{' '}
                    {isUpgradeCanVisible && (
                      <DiamondUpgradeIcon className="upgrade-icon" />
                    )}
                  </div>
                  <div className="description">
                    Only members you choose can access private folders.
                  </div>
                </button>
              </div>
            </UpgradePlanTooltip>
          </div>
        </div>
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            disabled={isSubmitting}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={isSubmitting}
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Create
          </Button>
        </div>
      </Form>
    </div>
  );
}
